import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, TextField } from '@mui/material';
import clsx from 'clsx';
import _ from 'lodash';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import type { CourseDetail } from '../../models';
import GlobalNotification from '../../component/common/snackbar';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import { VirtualizedMuiTable, type VirtualizedMuiTableColumn } from '../../component/table/virtualized-mui-table.component';
/** ic:baseline-delete-forever（套餐组明细行删除） */
import { ReactComponent as BaselineDeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';

export type CourseGroupDetailTableRow = CourseDetail;

type EditableField = 'price' | 'rule_lnr_max_num';

type DataColumn = {
    id: string;
    headerKey: string;
    renderCell: (row: CourseDetail, currency: string) => React.ReactNode;
};

/** 非行内编辑列（价格、单人限点单独处理） */
const COURSE_GROUP_DETAIL_STATIC_COLUMNS: DataColumn[] = [
    {
        id: 'menu_item_id',
        headerKey: 'google_text_id',
        renderCell: (r) => r.menu_item_id ?? '—',
    },
    {
        id: 'name',
        headerKey: 'global_text_name',
        renderCell: (r) => r.item_name1 ?? r.item_course_name ?? '—',
    },
    {
        id: 'unit',
        headerKey: 'menu_and_dishes_page_specification',
        renderCell: (r) => r.unit ?? '—',
    },
    {
        id: 'description',
        headerKey: 'google_text_remark',
        renderCell: (r) => r.item_description ?? '—',
    },
];

/** 编号 / 规格 / 价格 / 单人限点：偏窄；名称 / 备注：偏宽（备注列不设 width，吃满剩余） */
const COURSE_GROUP_DETAIL_COL_WIDTH_PX: Partial<Record<string, number>> = {
    menu_item_id: 68,
    name: 300,
    unit: 100,
};
const COURSE_GROUP_DETAIL_COL_WIDTH_PRICE = 120;
const COURSE_GROUP_DETAIL_COL_WIDTH_RULE = 130;

export type MenuLibraryCourseGroupDetailTableProps = {
    filteredDetails: CourseGroupDetailTableRow[];
    /** 已选明细的 `detail_id` 列表 */
    selectedRowIdx: number[];
    /** 勾选变化时回写整表选中集合（与 `VirtualizedMuiTable` 多选一致） */
    onSelectedRowIdxChange: (detailIds: number[]) => void;
    currency: string;
    /** 当前套餐组 id，用于 patch_course_detail */
    courseGroupId: number;
    /** 保存成功后回写本地 course_detail */
    onDetailUpdated: (detailId: number, patch: Pick<CourseDetail, 'price' | 'rule_lnr_max_num'>) => void;
    /** 删除单条套餐明细（`delete_course_detail`） */
    onDeleteDetailRow?: (detailId: number) => void | Promise<void>;
    /** 正在删除的明细 `detail_id`，仅该行显示 loading */
    deletingDetailRowId?: number | null;
    /** 批量删除进行中：禁用所有行内删除，但不显示行级 loading */
    batchDeleteInProgress?: boolean;
    /** 菜单库模块当前门店 `string_id`；`patch_course_detail` 路径需与所选门店一致 */
    rbpStringId?: string;
    /**
     * 云端草稿模式：行内改价/限点只回写本地，不调 `patch_course_detail`
     *（最终随菜品 `dishes/update` 的 `course_groups` 一并提交）。
     */
    draftMode?: boolean;
};

/**
 * 套餐组内菜品明细表：基于 VirtualizedMuiTable，列配置驱动数据列
 */
export function MenuLibraryCourseGroupDetailTable(props: MenuLibraryCourseGroupDetailTableProps) {
    const {
        filteredDetails,
        selectedRowIdx,
        onSelectedRowIdxChange,
        currency,
        courseGroupId,
        onDetailUpdated,
        onDeleteDetailRow,
        deletingDetailRowId = null,
        batchDeleteInProgress = false,
        rbpStringId,
        draftMode = false,
    } = props;

    const [editingOpen, setEditingOpen] = useState<null | { detailId: number; field: EditableField }>(null);
    const [editDraft, setEditDraft] = useState('');
    const [cellSaving, setCellSaving] = useState(false);
    /** 行内删除二次确认：待删 `detail_id` 与弹窗高亮名称 */
    const [singleDeleteConfirm, setSingleDeleteConfirm] = useState<null | { detailId: number; highlight: string }>(
        null,
    );

    const canEditRow = useCallback(
        (row: CourseDetail) => row.detail_id != null && courseGroupId > 0 && !cellSaving,
        [courseGroupId, cellSaving],
    );

    /** 列表刷新后若正在编辑的行已不存在，结束编辑态 */
    useEffect(() => {
        if (!editingOpen) {
            return;
        }
        const stillExists = filteredDetails.some((r) => r.detail_id === editingOpen.detailId);
        if (!stillExists) {
            setEditingOpen(null);
        }
    }, [filteredDetails, editingOpen]);

    const startEdit = useCallback(
        (row: CourseDetail, field: EditableField) => {
            if (!canEditRow(row)) {
                return;
            }
            const id = row.detail_id as number;
            if (field === 'price') {
                setEditDraft(row.price != null ? String(row.price) : '');
            } else {
                setEditDraft(row.rule_lnr_max_num != null ? String(row.rule_lnr_max_num) : '');
            }
            setEditingOpen({ detailId: id, field });
        },
        [canEditRow],
    );

    const commitEdit = useCallback(
        async (row: CourseDetail, field: EditableField, raw: string) => {
            if (row.detail_id == null || courseGroupId <= 0) {
                setEditingOpen(null);
                return;
            }
            let nextPrice = row.price ?? 0;
            let nextRule = row.rule_lnr_max_num ?? 9999;

            if (field === 'price') {
                const t = raw.trim().replace(/,/g, '.');
                if (t === '') {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    setEditingOpen(null);
                    return;
                }
                const n = Number(t);
                if (!Number.isFinite(n) || n < 0) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    setEditingOpen(null);
                    return;
                }
                nextPrice = Math.round(n * 100) / 100;
                if (Number(row.price ?? NaN) === nextPrice) {
                    setEditingOpen(null);
                    return;
                }
            } else {
                const t = raw.trim().replace(/\D/g, '');
                const n = t === '' ? NaN : parseInt(t, 10);
                if (!Number.isFinite(n) || n < 1) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    setEditingOpen(null);
                    return;
                }
                nextRule = n;
                if (Number(row.rule_lnr_max_num ?? NaN) === nextRule) {
                    setEditingOpen(null);
                    return;
                }
            }

            const patch = {
                price: field === 'price' ? nextPrice : row.price,
                rule_lnr_max_num: field === 'rule_lnr_max_num' ? nextRule : row.rule_lnr_max_num,
            };

            /** 云端草稿：只改内存，保存套餐时随 dishes/update 提交 */
            if (draftMode) {
                onDetailUpdated(row.detail_id!, patch);
                setEditingOpen(null);
                return;
            }

            const params: CourseDetail[] = [
                {
                    description: row.description,
                    detail_id: row.detail_id,
                    item_course_name: row.item_course_name,
                    menu_item_id: row.menu_item_id,
                    num: row.num ?? 1,
                    price: field === 'price' ? nextPrice : row.price,
                    unit: row.unit,
                    rule_lnr_max_num: field === 'rule_lnr_max_num' ? nextRule : row.rule_lnr_max_num,
                },
            ];

            setCellSaving(true);
            try {
                const result = await apiManage_rbp.patch_course_detail(courseGroupId, params, rbpStringId);
                if (_.get(result, 'error')) {
                    GlobalNotification.error(i18n.t('global_save_text_failed'));
                    return;
                }
                GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
                onDetailUpdated(row.detail_id!, patch);
                setEditingOpen(null);
            } finally {
                setCellSaving(false);
            }
        },
        [courseGroupId, draftMode, onDetailUpdated, rbpStringId],
    );

    const renderEditableCell = useCallback(
        (row: CourseDetail, field: EditableField) => {
            const id = row.detail_id;
            const isEditing = id != null && editingOpen?.detailId === id && editingOpen?.field === field;
            const interactive = canEditRow(row);

            if (isEditing) {
                return (
                    <TextField
                        size="small"
                        variant="outlined"
                        fullWidth
                        value={editDraft}
                        disabled={cellSaving}
                        onChange={(e) => setEditDraft(e.target.value)}
                        onBlur={(e) => void commitEdit(row, field, e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                (e.target as HTMLInputElement).blur();
                            }
                            if (e.key === 'Escape') {
                                setEditingOpen(null);
                            }
                        }}
                        autoFocus
                        inputProps={{
                            inputMode: field === 'price' ? 'decimal' : 'numeric',
                            autoComplete: 'off',
                        }}
                        className="menu-library-course-group-dialog__cell-editable-input"
                    />
                );
            }

            const display =
                field === 'price'
                    ? `${currency} ${row.price != null ? Number(row.price).toFixed(2) : '—'}`
                    : (row.rule_lnr_max_num ?? '—');

            return (
                <Box
                    component="span"
                    role={interactive ? 'button' : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    className={clsx(
                        'menu-library-course-group-dialog__cell-editable',
                        interactive && 'menu-library-course-group-dialog__cell-editable--interactive',
                    )}
                    onClick={() => interactive && startEdit(row, field)}
                    onKeyDown={(e) => {
                        if (!interactive) {
                            return;
                        }
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            startEdit(row, field);
                        }
                    }}
                >
                    {display}
                </Box>
            );
        },
        [currency, editingOpen, editDraft, cellSaving, canEditRow, startEdit, commitEdit],
    );

    const columns = useMemo((): VirtualizedMuiTableColumn<CourseGroupDetailTableRow>[] => {
        const unitCol = COURSE_GROUP_DETAIL_STATIC_COLUMNS.find((c) => c.id === 'unit')!;
        const beforeDesc = COURSE_GROUP_DETAIL_STATIC_COLUMNS.filter((c) => c.id !== 'unit' && c.id !== 'description');
        const descCol = COURSE_GROUP_DETAIL_STATIC_COLUMNS.find((c) => c.id === 'description')!;

        const staticMapped = (cols: DataColumn[]) =>
            cols.map(
                (col): VirtualizedMuiTableColumn<CourseGroupDetailTableRow> => ({
                    id: col.id,
                    width: COURSE_GROUP_DETAIL_COL_WIDTH_PX[col.id],
                    align: 'left',
                    header: i18n.t(col.headerKey),
                    renderCell: (r) => col.renderCell(r, currency),
                }),
            );

        const dataCols: VirtualizedMuiTableColumn<CourseGroupDetailTableRow>[] = [
            ...staticMapped(beforeDesc),
            {
                id: 'unit',
                width: COURSE_GROUP_DETAIL_COL_WIDTH_PX.unit,
                align: 'left',
                header: i18n.t(unitCol.headerKey),
                renderCell: (r) => unitCol.renderCell(r, currency),
            },
            {
                id: 'price',
                width: COURSE_GROUP_DETAIL_COL_WIDTH_PRICE,
                align: 'left',
                header: i18n.t('menu_and_dishes_page_price'),
                renderCell: (r) => renderEditableCell(r, 'price'),
            },
            {
                id: 'rule_lnr_max_num',
                width: COURSE_GROUP_DETAIL_COL_WIDTH_RULE,
                align: 'left',
                header: i18n.t('menu_and_dishes_page_rule_lnr_max_num'),
                renderCell: (r) => renderEditableCell(r, 'rule_lnr_max_num'),
            },
            {
                id: 'description',
                /** 不设 width，在 table-layout:fixed 下占满剩余空间 */
                align: 'left',
                header: i18n.t(descCol.headerKey),
                renderCell: (r) => descCol.renderCell(r, currency),
            },
            // 行内删除（delete_course_detail）
            {
                id: 'actions',
                width: 80,
                align: 'center',
                header: '',
                renderCell: (r) => {
                    const detailId = r.detail_id;
                    const canDelete = detailId != null && onDeleteDetailRow != null;
                    const isThisRowDeleting = detailId != null && deletingDetailRowId === detailId;
                    /** 单行或批量删除进行中时禁止重复点击；仅 `isThisRowDeleting` 行展示转圈 */
                    const deleteDisabled =
                        !canDelete || batchDeleteInProgress || deletingDetailRowId != null;
                    return (
                        <IconButton
                            type="button"
                            size="small"
                            aria-label={i18n.t('general_text_delete')}
                            disabled={deleteDisabled}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!canDelete || deleteDisabled || detailId == null || !onDeleteDetailRow) {
                                    return;
                                }
                                const highlight = String(
                                    r.item_name1 ?? r.item_course_name ?? r.menu_item_id ?? '—',
                                );
                                setSingleDeleteConfirm({ detailId, highlight });
                            }}
                            sx={{
                                '& svg': { width: 24, height: 24, display: 'block', color: 'var(--icon-neutral-800-a50)' },
                            }}
                        >
                            {isThisRowDeleting ? (
                                <CircularProgress color="inherit" size={24} thickness={5} />
                            ) : (
                                <BaselineDeleteForeverIcon aria-hidden />
                            )}
                        </IconButton>
                    );
                },
            },
        ];

        return dataCols;
    }, [currency, renderEditableCell, onDeleteDetailRow, deletingDetailRowId, batchDeleteInProgress]);

    return (
        <Box
            className="menu-library-course-group-dialog__virt-table-wrap"
            sx={{
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <VirtualizedMuiTable<CourseGroupDetailTableRow>
                className="menu-library-course-group-dialog__virt-table"
                tableClassName="menu-library-course-group-dialog__virt-table-inner"
                columns={columns}
                rows={filteredDetails}
                getRowKey={(r, i) =>
                    r.detail_id != null ? (r.detail_id as number) : `i-${i}-${r.menu_item_id ?? 'x'}`
                }
                checkboxSelection={{
                    selectedKeys: selectedRowIdx,
                    onChange: (keys) =>
                        onSelectedRowIdxChange(keys.filter((k): k is number => typeof k === 'number')),
                    isRowSelectable: (r) => r.detail_id != null,
                }}
                emptyText={i18n.t('general_text_no_or_more_data')}
                height="100%"
                defaultItemHeight={48}
                increaseViewportBy={80}
            />
            <AlertDialog
                open={singleDeleteConfirm != null}
                variant="delete"
                size="sm"
                title={i18n.t('menu_library_course_group_delete_detail_title')}
                bodyPrompt={i18n.t('alert_dialog_delete_confirm_question')}
                highlightText={singleDeleteConfirm?.highlight ?? ''}
                confirmText={i18n.t('general_text_delete')}
                onCancel={() => setSingleDeleteConfirm(null)}
                onConfirm={() => {
                    const pending = singleDeleteConfirm;
                    setSingleDeleteConfirm(null);
                    if (pending != null && onDeleteDetailRow) {
                        void onDeleteDetailRow(pending.detailId);
                    }
                }}
            />
        </Box>
    );
}
